import PlaybackSource from './timecodeSources/PlaybackSource';
import SocketSource from './timecodeSources/SocketSource';
import ExternalSource from './timecodeSources/ExternalSource';
import {dateToTimecode, tfsToDate, isTFS} from './timecodeSources/TimecodeUtils';
import MessageService from 'message-service/message-service';
import DataEvents from '@gameday/core/events/DataEvents';
const { NEW_TIMECODE, NEW_VALID_TIMECODE, TK_SOCKET_INITIATED_FAILOVER} = DataEvents;

export const timecodeSourceTypes = {
    SOCKET: 'SOCKET',
    PLAYBACK: 'PLAYBACK',
    EXTERNAL: 'EXTERNAL',
    DEFAULT: 'PLAYBACK'
}

const timecodeSourceMap = {
    [timecodeSourceTypes.SOCKET]: SocketSource,
    [timecodeSourceTypes.PLAYBACK]: PlaybackSource,
    [timecodeSourceTypes.EXTERNAL]: ExternalSource,
    [timecodeSourceTypes.DEFAULT]: PlaybackSource
}

const timecodeSourceSwapMap = {
    [timecodeSourceTypes.SOCKET]: timecodeSourceTypes.PLAYBACK,
    [timecodeSourceTypes.PLAYBACK]: timecodeSourceTypes.SOCKET
}

class Timekeeper {
    constructor (config) {
        this.config = config;
        this.currentTimeCode = null;
        this.previousTimecode = null;
        this.timecodeSourceType = timecodeSourceTypes.DEFAULT;
        this.timecodeSource = null;
        this.endTimecodeRequired = false;

        MessageService.on(NEW_TIMECODE, (payload) => {
            const { timecode, pushUpdateId, endTimecode } = payload;
            this.setCurrentTimecode(timecode, pushUpdateId, endTimecode);
        });
        MessageService.on(TK_SOCKET_INITIATED_FAILOVER, () => {
            this.fallbackTimekeeper();
        });
    }

    reset () {
        this.currentTimeCode = null;
        this.previousTimecode = null;
        this.timecodeSourceType = timecodeSourceTypes.DEFAULT;
        this.timecodeSource = null;
        this.config = null;
        this.endTimecodeRequired = false;
    }

    validateSocketGameState () {
        // we only want to open the socket if the same start time is within our window
        const {
            websocketConfig: {
                webSocketWindow
            },
            gameInfo: {
                dateTime
            }
        } = this.config;

        const now = new Date();
        const startTime = new Date(dateTime);

        const window = webSocketWindow * 60 * 60 * 1000; // convert WSW hours to MS
        const diff = Math.abs(now - startTime);

        const isValidStartTime = diff <= window;
        return isValidStartTime;
    }

    determineTimeCodeSourceType () {
        const {
            websocketConfig: {
                disableWebSockets
            }
        } = this.config;
        this.timecodeSourceType = timecodeSourceTypes.DEFAULT;
        if (this.config.dataMode === 'passive') { // NEED TO CHECK IF THIS IS 1:1 FOR ALL USE CASES
            this.timecodeSourceType = timecodeSourceTypes.EXTERNAL;
        } else if (disableWebSockets === true || !this.validateSocketGameState()) {
            this.timecodeSourceType = timecodeSourceTypes.PLAYBACK;
        } else {
            this.timecodeSourceType = timecodeSourceTypes.SOCKET;
        }
        this.timecodeSource = new timecodeSourceMap[this.timecodeSourceType](this);
    }

    startTimekeeping (timecode) {
        this.determineTimeCodeSourceType();
        // if we got a timecode from the URL, use that. Otherwise, use what we got back from the API
        this.start(timecode);
    }

    start (timecode) {
        if (this.timecodeSourceType === timecodeSourceTypes.EXTERNAL) {
            // If we're running in external mode we'll want to
            // make diff calls with a range of `startTimecode` to `endTimecode`
            this.endTimecodeRequired = true;
            this.timecodeSource.start();
        } else {
            let startingTimecode;
            const { initTimecode = null } = this.config;
            if (initTimecode != null) {
                // We started with an initTimecode, so we're not running live
                // Signal we want the endTimecode to support timecode ranges.
                this.endTimecodeRequired = true;
                startingTimecode = isTFS(this.config.initTimecode) ? tfsToDate(this.config.initTimecode) : this.config.initTimecode;
            } else {
                startingTimecode = isTFS(timecode) ? tfsToDate(timecode) : timecode;
            }
            this.currentTimeCode = startingTimecode;
            this.timecodeSource.start(this.currentTimeCode);
        }
    }

    stop () {
        this.timecodeSource.stop();
    }

    validateTimecode (timecode) {
        if (!this.currentTimeCode) {
            return true;
        }

        return new Date(timecode) > new Date(this.currentTimeCode);
    }

    setCurrentTimecode (timecode, pushUpdateId = null) {
        if (this.validateTimecode(timecode)) {
            this.previousTimecode = this.currentTimeCode;
            this.currentTimeCode = timecode;
            const currentTimecodeTFS = dateToTimecode(this.currentTimeCode);
            const previousTimecodeTFS = dateToTimecode(this.previousTimecode);
            // Socket is active
            if (pushUpdateId !== null) {
                MessageService.publish(NEW_VALID_TIMECODE, ({ startTimecode: currentTimecodeTFS, pushUpdateId }));
            } else {
                const timeCodes = {
                    startTimecode: previousTimecodeTFS
                };
                // If we are operating with a timecode range (passive PLAYBACK, or tfs PLAYBACK)
                // sent the endTimecode along for the request.
                // Otherwise we only want the startTimecode to allow catching all live incoming data
                if (this.endTimecodeRequired) {
                    timeCodes.endTimecode = currentTimecodeTFS;
                }
                MessageService.publish(NEW_VALID_TIMECODE, timeCodes);
            }
        }
    }

    updateConfig (updatedConfig) {
        this.config = updatedConfig;
    }

    fallbackTimekeeper () {
        const swappedSourceType = timecodeSourceSwapMap[this.timecodeSourceType];
        this.swapSources(swappedSourceType);
    }

    swapToExternalTimekeeper () {
        const swappedSourceType = timecodeSourceTypes.EXTERNAL;
        this.swapSources(swappedSourceType);
    }

    swapSources (swapToSourceType) {
        this.stop();
        this.timecodeSourceType = swapToSourceType;
        this.timecodeSource = new timecodeSourceMap[this.timecodeSourceType](this);
        this.start(this.currentTimeCode);
    }
}

export default Timekeeper;